'use client';

import type { SurfaceProfileDto } from '@terra/shared-contracts-external-network';
import { useTranslations } from 'next-intl';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsRow } from '../../settings/SettingsRow';
import { SettingsSection } from '../../settings/SettingsSection';
import {
  BUSINESS_PROFILE_TEXT_FIELD,
  type BusinessProfileTextField,
} from './business-profile.model';

export type BusinessProfileMetadataSectionProps = {
  profile: SurfaceProfileDto | null | undefined;
  isLoading: boolean;
  isError: boolean;
  isReadOnly: boolean;
  isDisabled: boolean;
  onRetry: () => void;
  onEditTextField: (field: BusinessProfileTextField) => void;
  onEditCompanySize: () => void;
};

export function BusinessProfileMetadataSection({
  profile,
  isLoading,
  isError,
  isReadOnly,
  isDisabled,
  onRetry,
  onEditTextField,
  onEditCompanySize,
}: BusinessProfileMetadataSectionProps) {
  const t = useTranslations('designSystem.networkSettings.businessProfile');
  const tCommon = useTranslations('designSystem.common');
  const companySizeValue = profile?.companySize
    ? t(`companySize.${profile.companySize}`)
    : t('emptyValue');

  return (
    <SettingsSection title={t('metadata.title')}>
      <SettingsCard>
        {isError ? (
          <SettingsRow
            label={tCommon('error')}
            value={t('fetchError')}
            action={{
              label: t('tryAgain'),
              onClick: onRetry,
              disabled: isDisabled,
            }}
            isReadOnly={isReadOnly}
          />
        ) : null}
        <SettingsRow
          label={t('metadata.description')}
          tooltip={t('metadata.tooltips.description')}
          value={profile?.description ?? t('emptyValue')}
          action={{
            label: tCommon('edit'),
            onClick: () =>
              onEditTextField(BUSINESS_PROFILE_TEXT_FIELD.DESCRIPTION),
            disabled: isDisabled,
          }}
          isReadOnly={isReadOnly}
          isLoading={isLoading}
        />
        <SettingsRow
          label={t('metadata.primaryIndustry')}
          tooltip={t('metadata.tooltips.primaryIndustry')}
          value={profile?.primaryIndustry ?? t('emptyValue')}
          action={{
            label: tCommon('edit'),
            onClick: () =>
              onEditTextField(BUSINESS_PROFILE_TEXT_FIELD.PRIMARY_INDUSTRY),
            disabled: isDisabled,
          }}
          isReadOnly={isReadOnly}
          isLoading={isLoading}
        />
        <SettingsRow
          label={t('metadata.companySize')}
          tooltip={t('metadata.tooltips.companySize')}
          value={companySizeValue}
          action={{
            label: tCommon('edit'),
            onClick: onEditCompanySize,
            disabled: isDisabled,
          }}
          isReadOnly={isReadOnly}
          isLoading={isLoading}
        />
      </SettingsCard>
    </SettingsSection>
  );
}
